import { ActionItemStatus } from "@/store/models/clientUpdates/actionItem";
import { ActionItemsSearchDataModel } from "@/store/models/clientUpdates/actionItemsSearchData";
import ClientUpdatesModule from "@/store/modules/clientUpdates.module";
import { toaster } from "@/utilities/toaster";
import { assign, Machine, send } from "xstate";
export const tabStatus = (tab) => {
    if (tab === "completed") {
        return ActionItemStatus.Completed;
    }
    return tab === "closed" ? ActionItemStatus.Closed : ActionItemStatus.Open;
};
const actionItemsMachine = Machine({
    id: "actionItems",
    type: "parallel",
    context: {
        activeTab: "open",
        searchData: new ActionItemsSearchDataModel(),
        actionItemFormMode: "add",
    },
    states: {
        // Action Items grid - server-side status tab, column filters, sorting and paging
        gridInfo: {
            initial: "gettingActionItems",
            on: {
                SEARCH: { target: ".gettingActionItems", actions: "assignSearchData" },
                SELECT_TAB: { target: ".gettingActionItems", actions: ["assignTab", "assignSearchData"] },
                REFRESH_GRID: { target: ".gettingActionItems" },
            },
            states: {
                idle: {},
                gettingActionItems: {
                    invoke: {
                        id: "gettingActionItems",
                        src: (context, event) => {
                            return ClientUpdatesModule.GetActionItems({ ...context.searchData, status: tabStatus(context.activeTab) });
                        },
                        onDone: { target: "loadedActionItems" },
                        onError: { target: "idle" },
                    },
                },
                loadedActionItems: {},
            },
        },
        // Add/Edit Action Item modal
        actionItemInfo: {
            initial: "idle",
            states: {
                idle: {
                    on: {
                        ADD_ACTION_ITEM: { target: "actionItemFormOpen", actions: "setActionItemFormAdd", cond: "canAddActionItem" },
                        EDIT_ACTION_ITEM: { target: "actionItemFormOpen", actions: "setActionItemFormEdit" },
                    },
                },
                actionItemFormOpen: {
                    on: {
                        SAVE_ACTION_ITEM: { target: "savingActionItem" },
                        CLOSE_ACTION_ITEM_MODAL: { target: "idle" },
                    },
                },
                savingActionItem: {
                    invoke: {
                        id: "savingActionItem",
                        src: (context, event) => {
                            return context.actionItemFormMode === "edit" ? ClientUpdatesModule.EditActionItem(event.payLoad) : ClientUpdatesModule.AddActionItem(event.payLoad);
                        },
                        onDone: { target: "idle", actions: ["showSuccessMessage", "refreshGrid"] },
                        onError: { target: "actionItemFormOpen" },
                    },
                },
            },
        },
        // Delete confirm
        deleteInfo: {
            initial: "idle",
            states: {
                idle: {
                    on: {
                        DELETE_ACTION_ITEM: { target: "deleteActionItemConfirm" },
                    },
                },
                deleteActionItemConfirm: {
                    on: {
                        DELETING_ACTION_ITEM: { target: "deletingActionItem" },
                        CLOSE_MODAL: { target: "idle" },
                    },
                },
                deletingActionItem: {
                    invoke: {
                        id: "deletingActionItem",
                        src: (context, event) => {
                            return ClientUpdatesModule.DeleteActionItem(event.payLoad);
                        },
                        onDone: { target: "idle", actions: ["showSuccessMessage", "stepBackFromEmptiedPage", "refreshGrid"] },
                        onError: { target: "idle" },
                    },
                },
            },
        },
        // Complete / reopen - the item then leaves the current tab
        toggleInfo: {
            initial: "idle",
            states: {
                idle: {
                    on: {
                        TOGGLE_ACTION_ITEM: { target: "togglingActionItem" },
                    },
                },
                togglingActionItem: {
                    invoke: {
                        id: "togglingActionItem",
                        src: (context, event) => {
                            // A Completed or Closed item is reopened; an Open one is completed
                            return event.payLoad.status === ActionItemStatus.Open
                                ? ClientUpdatesModule.CompleteActionItem(event.payLoad.id)
                                : ClientUpdatesModule.ReopenActionItem(event.payLoad.id);
                        },
                        onDone: { target: "idle", actions: ["showSuccessMessage", "stepBackFromEmptiedPage", "refreshGrid"] },
                        onError: { target: "idle" },
                    },
                },
            },
        },
        // Close confirm - the item then moves to the Closed tab
        closeInfo: {
            initial: "idle",
            states: {
                idle: {
                    on: {
                        CLOSE_ACTION_ITEM: { target: "closeActionItemConfirm" },
                    },
                },
                closeActionItemConfirm: {
                    on: {
                        CLOSING_ACTION_ITEM: { target: "closingActionItem" },
                        CANCEL_CLOSE_ACTION_ITEM: { target: "idle" },
                    },
                },
                closingActionItem: {
                    invoke: {
                        id: "closingActionItem",
                        src: (context, event) => {
                            return ClientUpdatesModule.CloseActionItem(event.payLoad);
                        },
                        onDone: { target: "idle", actions: ["showSuccessMessage", "stepBackFromEmptiedPage", "refreshGrid"] },
                        onError: { target: "idle" },
                    },
                },
            },
        },
        // View Update popover (update-linked action items only)
        updatePopInfo: {
            initial: "idle",
            on: {
                VIEW_UPDATE: { target: ".gettingUpdate" },
                CLOSE_UPDATE: { target: ".idle" },
            },
            states: {
                idle: {},
                gettingUpdate: {
                    invoke: {
                        id: "gettingUpdate",
                        src: (context, event) => {
                            return ClientUpdatesModule.GetUpdateSummary(event.updateId);
                        },
                        onDone: { target: "updateOpen" },
                        onError: { target: "idle" },
                    },
                },
                updateOpen: {},
            },
        },
        // Client options for the modal's client picker, loaded once on start
        pageDataInfo: {
            initial: "gettingPageData",
            states: {
                gettingPageData: {
                    invoke: {
                        id: "gettingPageData",
                        src: (context, event) => {
                            return ClientUpdatesModule.GetUpdatesPageData();
                        },
                        onDone: { target: "loadedPageData" },
                        onError: { target: "idle" },
                    },
                },
                loadedPageData: {},
                idle: {},
            },
        },
    },
}, {
    actions: {
        showSuccessMessage: (context, event) => {
            if (event.data && event.data.successMessages && event.data.successMessages.length) {
                toaster.ShowSuccess(event.data.successMessages[0]);
            }
        },
        assignTab: assign({
            activeTab: (context, event) => event.tab,
        }),
        assignSearchData: assign({
            searchData: (context, event) => event.searchData,
        }),
        // The removed item was the only row on a later page - show the previous page instead of an empty one
        stepBackFromEmptiedPage: assign({
            searchData: (context, event) => {
                const page = context.searchData.page;
                return page > 1 && (ClientUpdatesModule.actionItemGrid || []).length <= 1 ? { ...context.searchData, page: page - 1 } : context.searchData;
            },
        }),
        setActionItemFormAdd: assign({
            actionItemFormMode: (context, event) => "add",
        }),
        setActionItemFormEdit: assign({
            actionItemFormMode: (context, event) => "edit",
        }),
        refreshGrid: send({ type: "REFRESH_GRID" }),
    },
    guards: {
        canAddActionItem: (context) => context.activeTab === "open",
    },
    activities: {},
    services: {},
});
export default actionItemsMachine;
